import { useState } from 'react';

import { useGetTenantsQuery } from '@features/tenants';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import type { CreateApplicationRequest, Tenant } from '@/types';
import { logger } from '@/utils/logger';

import { useCreateApplicationMutation } from '../api/appsApi';

const projectSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(100, 'Project name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  tenantId: z.string().min(1, 'Organization is required'),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
}

export const NewProjectModal = ({ open, onClose }: NewProjectModalProps) => {
  const navigate = useNavigate();
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: tenants, isLoading: tenantsLoading } = useGetTenantsQuery();
  const [createApplication, { isLoading: isCreating }] = useCreateApplicationMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      description: '',
      tenantId: '',
    },
  });

  const handleClose = () => {
    if (!isCreating) {
      reset();
      setCreateError(null);
      onClose();
    }
  };

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  const onSubmit = async (data: ProjectFormData) => {
    setCreateError(null);

    try {
      const appData: CreateApplicationRequest = {
        name: data.name,
        description: data.description || undefined,
      };

      const result = await createApplication({
        tenantId: data.tenantId,
        data: appData,
      }).unwrap();

      // Navigate to the new project's dashboard
      navigate(`/a/${result.id}/dash`);
      handleClose();
    } catch (error) {
      logger.error('Failed to create project:', error);
      setCreateError(
        error && typeof error === 'object' && 'data' in error
          ? String((error as { data?: { message?: string } }).data?.message)
          : 'Failed to create project. Please try again.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Create New Project</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}

            <TextField
              {...register('name')}
              label="Project Name"
              placeholder="My Project"
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={isCreating}
              autoFocus
            />

            <TextField
              {...register('description')}
              label="Description"
              placeholder="Describe what this project is for..."
              fullWidth
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description?.message}
              disabled={isCreating}
            />

            <FormControl fullWidth required error={!!errors.tenantId} disabled={isCreating}>
              <InputLabel>Organization</InputLabel>
              <Controller
                name="tenantId"
                control={control}
                render={({ field }) => {
                  const renderMenuItems = () => {
                    if (tenantsLoading) {
                      return (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading organizations...
                        </MenuItem>
                      );
                    }

                    if (!tenants || tenants.length === 0) {
                      return <MenuItem disabled>No organizations available</MenuItem>;
                    }

                    return tenants.map((tenant: Tenant) => (
                      <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.name}
                      </MenuItem>
                    ));
                  };

                  return (
                    <Select {...field} label="Organization">
                      {renderMenuItems()}
                    </Select>
                  );
                }}
              />
              {errors.tenantId && <FormHelperText>{errors.tenantId.message}</FormHelperText>}
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="error" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating || tenantsLoading}
            startIcon={isCreating ? <CircularProgress size={16} /> : null}
          >
            {isCreating ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
